import { connect } from 'react-redux';
import localStorageWrapper from 'utils/localStorage';
import { getEventsApiClient } from 'lib/SplashApiClient/subclients/EventsApiClient';
import { APP } from '../index';
import { getEventUpdates } from './selectors';

/**
 * Event Updates
 *
 * This module represents changes in event data since the user's last viewing.
 * In its current implementation, it is based on the user's last page view. In the future
 * this module could be utilized to incoporate updates from web sockets or polling.
 */

// Constants
export const EVENT_UPDATES = 'eventUpdates';

// Utils

// Using `btoa` so we're not storing event ids in plain text ¯\_(ツ)_/¯
export const getAttendeeTotalLocalStorageKey = (eventId) => `attendee-total-${window.btoa(eventId)}`;

// Storage key is based on, but not exactly the eventId
export function setPreviousAttendeeTotalInStorage(eventId, previousAttendeeTotal = 0) {
    const attendeeKey = getAttendeeTotalLocalStorageKey(eventId);
    localStorageWrapper.setItem(attendeeKey, previousAttendeeTotal);
}

// Action keys
export const RESET_EVENT_UPDATES = `${APP}/RESET_EVENT_UPDATES`;
export const SET_PREVIOUS_ATTENDEE_TOTAL = `${APP}/SET_PREVIOUS_ATTENDEE_TOTAL`;
export const SET_ATTENDEE_TOTAL = `${APP}/SET_ATTENDEE_TOTAL`;

// Reducers
const INITIAL_STATE = {
    [EVENT_UPDATES]: {
        previousAttendeeTotal: 0,
        attendeeTotal: 0,
        isLoading: false,
    },
};

export const reducers = {
    [SET_PREVIOUS_ATTENDEE_TOTAL](state, { previousAttendeeTotal, isLoading }) {
        return {
            ...state,
            [EVENT_UPDATES]: {
                ...state[EVENT_UPDATES],
                previousAttendeeTotal,
                isLoading,
            },
        };
    },
    [SET_ATTENDEE_TOTAL](state, { attendeeTotal }) {
        return {
            ...state,
            [EVENT_UPDATES]: {
                ...state[EVENT_UPDATES],
                attendeeTotal,
                isLoading: false,
            },
        };
    },
    [RESET_EVENT_UPDATES](state) {
        return {
            ...state,
            [EVENT_UPDATES]: INITIAL_STATE,
        };
    },
};

// Actions
export function setPreviousAttendeeTotal(eventId, previousAttendeeTotal, isLoading = false) {
    // Storing the current attendee total in local storage for the next page load
    setPreviousAttendeeTotalInStorage(eventId, previousAttendeeTotal);

    return {
        type: SET_PREVIOUS_ATTENDEE_TOTAL,
        previousAttendeeTotal,
        isLoading,
    };
}

export function setAttendeeTotal(attendeeTotal) {
    return {
        type: SET_ATTENDEE_TOTAL,
        attendeeTotal,
    };
}

// Thunks
export const fetchAttendeeTotal = (eventId) => (dispatch) => {
    const previousAttendeeTotal =
        parseInt(localStorageWrapper.getItem(getAttendeeTotalLocalStorageKey(eventId)), 10) || 0;
    dispatch(setPreviousAttendeeTotal(eventId, previousAttendeeTotal, true));

    return getEventsApiClient()
        .getEventAttendeeCounts(eventId)
        .then((res) => {
            const counts = res.data || [];
            const attendeeTotal = counts.find((count) => count.name === 'rsvp_yes').count;
            if (typeof attendeeTotal === 'number' && attendeeTotal >= 0) {
                dispatch(setAttendeeTotal(attendeeTotal));
            }

            return attendeeTotal;
        });
};

// HOC
function mapStateToProps(state) {
    return {
        eventUpdates: getEventUpdates(state),
    };
}

export const withEventUpdates = (Component) => connect(mapStateToProps)(Component);

// App module
export default {
    initialState: INITIAL_STATE,
    reducers,
};
